import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import uniq from 'lodash/uniq';
import { BAM_PAYWALL_IFRAME_ID } from 'constants';
import { AuthTypes } from 'enums';
import WatchPropTypes from 'common/PropTypes';
import {
    createSelectProviderAction,
    createAndPerformStartAuthenticationAction,
    createDisplayProviderDialogAction,
    createAndPerformLogoutAction,
    createDTCPaywallAction
} from 'common/actions/authenticationActions';
import { updateRoute } from 'common/actions/appActions';
import { newRelicTrackEvent } from 'lib/api/newRelicApi';
import { isEmpty } from 'lib/util';
import {
    authStateKnown,
    canPlayContent,
    canPlayStream,
    streamHasAuthType
} from 'lib/auth';
import {
    handleMultipleStreamAuthentication,
    handleSingleStreamAuthentication
} from 'lib/streamPicker';
import {
    adobePassSelector,
    authenticationEnabledSelector,
    checkAuthenticationCompleteSelector,
    preauthorizedResourcesSelector,
    userAuthSelector,
    bamSubReadySelector
} from 'common/selectors/authenticationSelectors';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import selectedStreamSelector from 'common/selectors/selectedStreamSelector';
import routingSelector from 'common/selectors/routingSelector';
// import { newRelicTrackingSelector } from 'common/selectors/appSelector';
import pickerStreamsSelector from 'common/selectors/pickerStreamsSelector';
import { oneIdTokenSelector } from 'common/selectors/oneIdSelector';

export default function authenticationAware(options = {}) {
    /**
     * Currently options is not being passed from authenticationAware caller.
     * createDisplayProviderDialogAction is just assigned to nonAuthedActionCreator
     * We don't need to do the following assignment if we remove options arg
     */
    const { nonAuthedActionCreator = createDisplayProviderDialogAction } = options;

    return function wrap(WrappedComponent) {
        class AuthenticationAware extends Component {
            constructor(props) {
                super(props);
                this.providerSelect = this.providerSelect.bind(this);
                this.adobePassLogout = this.adobePassLogout.bind(this);
                this.newRelicTrackingOnStreamAuth = this.newRelicTrackingOnStreamAuth.bind(this);
                this.newRelicTrackingOnSelectStream = this.newRelicTrackingOnSelectStream.bind(this);
                this.state = {
                    isPaywallRendered: false
                };
            }

            componentWillReceiveProps({
                adobePass: nextAdobePass = {},
                authenticationEnabled,
                checkAuthenticationComplete,
                selectedContent: nextSelectedContent,
                selectedStream: nextSelectedStream,
                oneIdToken,
                userAuth,
                bamSubReady: nextBamSubReady
            }) {
                const {
                    dispatch,
                    adobePass: {
                        checkAuthentication: { completeTime } = {}
                    } = {},
                    selectedContent,
                    selectedStream,
                    routing: { locationBeforeTransitions }
                } = this.props;
                const { query = {}, pathname } = locationBeforeTransitions || {};
                const { checkAuthentication: { completeTime: nextCompleteTime } = {} } = nextAdobePass;
                const { status } = nextSelectedContent;
                const isUpcoming = status === 'upcoming';

                if (isEmpty(nextSelectedContent)) {
                    return;
                }

                const loginWithContentID = query.login_with_content_eventId || query.login_with_content_id;
                // logic for realoding page after OneID login triggered by Paywall(`LOGIN_REQUESTED`)
                // trigger paywall again and updateRoute if following condition is met after user is logged in
                if (loginWithContentID) {
                    const authTypes = [AuthTypes.BAM];
                    const packages = query.login_with_pkg && query.login_with_pkg.split(',');
                    const streamPlaywableAfterLogin = canPlayStream({ authTypes, packages }, userAuth, false);
                    const cannotPlay = isUpcoming || (authStateKnown(userAuth) && !streamPlaywableAfterLogin);

                    if (cannotPlay || authStateKnown(userAuth) || (nextBamSubReady && !userAuth.length)) {
                        if (!packages || !packages.length) {
                            return;
                        }

                        // trigger paywall if it is upcoming event and if user
                        // can't play the content after DSS sub check is complete
                        if (!this.state.isPaywallRendered && (cannotPlay || (nextBamSubReady && !streamPlaywableAfterLogin))) {
                            const contentId = query.login_with_content_eventId || query.login_with_content_id;
                            const streamId = query.login_with_id || contentId;
                            const stream = streamId && { id: streamId };
                            /**
                             * https://espnjira.disney.com/browse/WEWEB-2501
                             * Fix background image missing in paywallConfig for unpaid E+ user on login
                             * pass nextSelectedContent to paywall instead of just contentId,
                             * so that DSS can get the background image from paywallConfig
                             */
                            dispatch(createDTCPaywallAction({
                                containerId: BAM_PAYWALL_IFRAME_ID,
                                content: nextSelectedContent,
                                stream
                            }));
                            this.setState({
                                isPaywallRendered: true
                            });
                        }
                        // trigger route change: removing login params.
                        const newQuery = { ...query };
                        newQuery.login_with_content_eventId = undefined;
                        newQuery.login_with_content_id = undefined;
                        newQuery.login_with_id = undefined;
                        newQuery.login_with_pkg = undefined;
                        dispatch(updateRoute(newQuery));
                        return;
                    }
                }

                let checkAuthenticationStateComplete = completeTime !== nextCompleteTime;

                /**
                 * ticket: https://jira.disney.com/browse/WEWEB-2745
                 * When users right click open on upcoming content on a new tab, we need to check if oneId getGuest
                 * event is completed (oneIdToken not undefined). We need to pass oneId token along with auth state to DSS so that
                 * they can render the paywall without sign up button on the paywall if users are entitled
                 * add isPaywallRendered state check to prevent paywall rendering more than one
                 */

                if (isUpcoming && !this.state.isPaywallRendered) {
                    if (oneIdToken || (checkAuthenticationStateComplete && !userAuth.length)) {
                        checkAuthenticationStateComplete = true;
                        this.setState({
                            isPaywallRendered: true
                        });
                    } else {
                        checkAuthenticationStateComplete = false;
                    }
                }

                const newContentSelected = selectedContent !== nextSelectedContent;
                const newStreamSelected = (
                    !newContentSelected &&
                    (!isEmpty(nextSelectedStream) && selectedStream !== nextSelectedStream)
                );
                const chooseTVEAuth = query.auth === 'tve';
                const eventOnHero = query.eventCalendarId && pathname.indexOf('/player') === -1;
                // if check authentication completes and a piece of content was already selected,
                // or if check authentication has completed already and user clicked on content, perform authentication
                // add loginWithContentID check to prevent rendering authentication flow again
                // because of getting entitlement deplay from DSS
                const newContentClick = (
                    // user clicked playable item(hero, tile, schedule link, etc), or eventCalendarId is passed on non-player page
                    (!nextSelectedContent.disableAuthPrompt || chooseTVEAuth || eventOnHero) &&
                    !loginWithContentID && !canPlayContent(nextSelectedContent, userAuth, false) &&
                    (
                        checkAuthenticationStateComplete ||
                        (newContentSelected && checkAuthenticationComplete)
                    )
                );
                const newStreamFeedSelect = (
                    !canPlayStream(nextSelectedStream, userAuth, false) && // no streams are playable with user's auth state
                    (newStreamSelected && checkAuthenticationComplete)
                );

                if (
                    authenticationEnabled && // authentication is enabled
                    (newContentClick || newStreamFeedSelect)
                ) {
                    if (newStreamFeedSelect) {
                        if (
                            streamHasAuthType(nextSelectedStream, AuthTypes.MVPD) ||
                            streamHasAuthType(nextSelectedStream, AuthTypes.ISP)
                        ) { // TVE-only content
                            dispatch(createAndPerformStartAuthenticationAction({
                                content: nextSelectedContent,
                                stream: nextSelectedStream
                            }))
                                .then((isAuthenticated) => {
                                    if (!isAuthenticated) {
                                        dispatch(nonAuthedActionCreator());
                                    }
                                });
                        } else if (streamHasAuthType(nextSelectedStream, AuthTypes.BAM)) {
                            if (!this.state.isPaywallRendered) {
                                dispatch(createDTCPaywallAction({
                                    containerId: BAM_PAYWALL_IFRAME_ID,
                                    content: nextSelectedContent,
                                    stream: nextSelectedStream
                                }));
                            }
                        }

                        return;
                    }

                    const { streams = [], links: { picker } = {} } = nextSelectedContent;
                    if (!streams.length) {
                        return;
                    }

                    // comment out newrelic tracking until tracking is required for new stream picker
                    // const newRelicTrackFields = {
                    //     eventId,
                    //     userAuth: userAuth.length ? userAuth.join('|') : userAuth,
                    //     action: 'MultipleStreamAuth',
                    //     streams: streams.length ? streams.map(item => item.id).join('|') : '',
                    //     streamCount: streams.length
                    // };

                    const showPicker = picker !== undefined;
                    /*
                    * NOTE: Handles multiple streams scenario without `id` query parameter.
                    * If the `id` query parameter is defined, we match by single stream
                    */
                    if (showPicker && !query.id) {
                        const { preauthorizedResources, country, lang, pickerStreams } = this.props;
                        const authRes = { userAuth, preauthorizedResources };
                        handleMultipleStreamAuthentication({
                            lang,
                            country,
                            nextSelectedContent,
                            dispatch,
                            showPicker,
                            pickerStreams,
                            authRes,
                            locationBeforeTransitions
                        });
                        // newRelicTrackFields.action = 'MultipleStreamAuth';
                    } else {
                        let singleStream = streams;
                        if (query.id && streams.length > 1) {
                            singleStream = streams.filter(item => item.id === query.id);
                        }
                        const requiredAuths = uniq(singleStream.reduce((acc, { authTypes }) => (acc.concat(authTypes)), []));

                        handleSingleStreamAuthentication({
                            requiredAuths,
                            nextSelectedContent,
                            query,
                            dispatch,
                            stream: singleStream[0]
                        });
                        // newRelicTrackFields.action = 'SingleStreamAuth';
                    }

                    // comment out newrelic tracking until tracking is required for new stream picker
                    // if (isNewrelicTrackingOn) {
                    //     this.newRelicTrackingOnStreamAuth(newRelicTrackFields);
                    // }
                }
            }

            providerSelect(provider) {
                this.props.dispatch(createSelectProviderAction(provider));
            }

            adobePassLogout() {
                const { selectedStream, dispatch } = this.props;
                return dispatch(createAndPerformLogoutAction(selectedStream));
            }

            async newRelicTrackingOnStreamAuth(data) {
                const {
                    env,
                    device,
                    newrelicInsertKey,
                    newrelicAccountId,
                    oneId
                 } = this.props;

                const param = {
                    newrelicInsertKey,
                    newrelicAccountId,
                    trackingData: {
                        env,
                        device,
                        ...data,
                        swid: oneId.swid,
                        eventType: 'PlaybackFlow',
                        devicePlatform: 'watchWeb'
                    }
                };
                await newRelicTrackEvent(param);
            }

            // we pass down this method to the child component (BroadcastPicker) via props
            async newRelicTrackingOnSelectStream(stream) {
                const {
                    env,
                    device,
                    newrelicInsertKey,
                    newrelicAccountId,
                    oneId
                 } = this.props;
                const { id, content, streams } = stream;
                const param = {
                    newrelicInsertKey,
                    newrelicAccountId,
                    trackingData: {
                        env,
                        device,
                        eventId: content ? content.eventId : null,
                        action: 'UserSelectedStream',
                        streams: id,
                        streamCount: streams ? streams.length : 1,
                        swid: oneId.swid,
                        eventType: 'PlaybackFlow',
                        devicePlatform: 'watchWeb'
                    }
                };
                await newRelicTrackEvent(param);
            }

            render() {
                const { isNewrelicTrackingOn } = this.props;
                const onSelectStreamTracking = isNewrelicTrackingOn ? this.newRelicTrackingOnSelectStream : null;
                return (
                    <WrappedComponent
                        {...this.props}
                        providerSelect={this.providerSelect}
                        adobePassLogout={this.adobePassLogout}
                        onSelectStreamTracking={onSelectStreamTracking}
                    />
                );
            }
        }

        AuthenticationAware.defaultProps = {
            selectedContent: {}
        };

        AuthenticationAware.propTypes = {
            adobePass: PropTypes.object,
            authenticationEnabled: PropTypes.bool,
            checkAuthenticationComplete: PropTypes.bool,
            dispatch: PropTypes.func,
            selectedContent: WatchPropTypes.content,
            selectedStream: WatchPropTypes.stream,
            routing: PropTypes.object,
            oneId: PropTypes.object,
            device: PropTypes.string,
            newrelicInsertKey: PropTypes.string,
            newrelicAccountId: PropTypes.string,
            env: PropTypes.string,
            isNewrelicTrackingOn: PropTypes.bool,
            preauthorizedResources: PropTypes.array,
            userAuth: PropTypes.array,
            lang: PropTypes.string,
            country: PropTypes.string,
            pickerStreams: PropTypes.array,
            bamSubReady: PropTypes.bool,
            oneIdToken: PropTypes.object
        };

        const mapStateToProps = (state) => {
            const {
                env,
                lang,
                country,
                device,
                newrelicInsertKey,
                newrelicAccountId
            } = state.app;
            return {
                env,
                lang,
                country,
                device,
                newrelicInsertKey,
                newrelicAccountId,
                adobePass: adobePassSelector(state),
                oneIdToken: oneIdTokenSelector(state),
                authenticationEnabled: authenticationEnabledSelector(state),
                checkAuthenticationComplete: checkAuthenticationCompleteSelector(state),
                selectedContent: selectedContentSelector(state),
                selectedStream: selectedStreamSelector(state),
                providerDialog: state.providerDialog,
                userAuth: userAuthSelector(state),
                routing: routingSelector(state),
                oneId: state.oneId,
                // isNewrelicTrackingOn: newRelicTrackingSelector(state),
                preauthorizedResources: preauthorizedResourcesSelector(state),
                pickerStreams: pickerStreamsSelector(state),
                bamSubReady: bamSubReadySelector(state)
            };
        };

        return connect(mapStateToProps)(AuthenticationAware);
    };
}



// WEBPACK FOOTER //
// ./app/webapp/common/authenticationAware.js